import type { SuperBillingView } from '@tandemone/types'
import { isCompedSubscription } from '@tandemone/types'

import { Badge } from '@/components/ui/badge'

import { formatBillingDate } from './plan-price'

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex flex-col gap-0.5 py-1.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4'>
      <span className='text-[13px] text-ink-soft'>{label}</span>
      {/* stripe ids have no break point of their own. */}
      <span className='text-[13px] font-medium break-all text-ink sm:break-normal'>
        {value}
      </span>
    </div>
  )
}

function date(iso: string | null): string {
  return formatBillingDate(iso)
}

/* answers "why is this company gated", with the actions that fix it below. the
 * page owns the fetch, since it addresses the company by slug. */
export function SuperBillingPanel({ view }: { view: SuperBillingView }) {
  const { entitlements: ent, subscription, seatsUsed } = view
  const comped = isCompedSubscription(subscription)

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-4 rounded-xl border border-border p-5'>
        <div className='flex items-center justify-between gap-3'>
          <h2 className='font-display text-[20px] leading-none text-ink'>
            Plan
          </h2>
          <div className='flex items-center gap-2'>
            {comped && <Badge variant='outline'>Comped</Badge>}
            <Badge variant='outline'>{ent.planLabel}</Badge>
          </div>
        </div>

        <div className='flex flex-col divide-y divide-border'>
          <Row label='Effective plan' value={ent.planLabel} />
          <Row label='Status' value={ent.status} />
          <Row
            label='Seats'
            value={
              ent.seatLimit === null
                ? `${seatsUsed} (unlimited)`
                : `${seatsUsed} of ${ent.seatLimit}`
            }
          />
          {/* a comp with no expiry has no date to show, and "never" is the
              answer support is actually asking for. */}
          <Row
            label={comped ? 'Comp expires' : 'Renews'}
            value={
              comped && ent.currentPeriodEnd === null
                ? 'Never'
                : date(ent.currentPeriodEnd)
            }
          />
          <Row
            label='Bills by'
            value={
              view.collectionMethod === 'send_invoice' ? 'Invoice' : 'Card'
            }
          />
          {subscription?.stripeSubscriptionId && (
            <Row
              label='Stripe subscription'
              value={subscription.stripeSubscriptionId}
            />
          )}
          {subscription?.grantNote && (
            <Row label='Comp reason' value={subscription.grantNote} />
          )}
        </div>
      </div>

      {/* read-only on purpose: the super routes for comp, revoke, resync,
          cancel and collection method still exist, but nothing calls them, so
          those changes happen in the Stripe dashboard. */}
    </div>
  )
}
